// Enrollment naming rules, shared by anything that creates a new
// self-directed enrollment from a course (bundle builder, Reallocate Hours).
// Mirrors the server (api packages.controller namedInSetup / validateBundle
// and enrollments/hour_reallocation.js validateTargets):
//
//  - allow_custom_entry courses (e.g. Educational Book Review) are named in
//    the setup wizard — no name is collected up front and repeats are fine
//  - custom1/custom2 option courses assemble prefix + chosen parts ("N/A"
//    stripped) and must pick every offered part
//  - everything else is the course title plus an optional free-text focus
//  - effective names must be unique across ALL of a student's non-deleted
//    enrollments (custom_name || course title), case-insensitive

export function normalizeName(value) {
    return String(value || '').replace(/\s+/g, ' ').trim();
}

function namingConfig(course) {
    const cfg = course && course.self_directed_config;
    return (cfg && cfg.custom_naming) || null;
}

export function isNamedInSetup(course) {
    const naming = namingConfig(course);
    return !!(naming && naming.allow_custom_entry);
}

// The dropdown-style naming config, or null when the course is free-text /
// named in setup
export function optionNaming(course) {
    const naming = namingConfig(course);
    return (naming && !naming.allow_custom_entry && ((naming.custom1_options || []).length || (naming.custom2_options || []).length)) ? naming : null;
}

export function suffixOptions(course, which) {
    const naming = optionNaming(course);
    const list = naming && naming[which === 2 ? 'custom2_options' : 'custom1_options'];
    return Array.isArray(list) ? list : [];
}

// Every offered dropdown has a pick
export function optionNamingComplete(course, custom1, custom2) {
    if (!optionNaming(course)) return true;
    if (suffixOptions(course, 1).length && !custom1) return false;
    if (suffixOptions(course, 2).length && !custom2) return false;
    return true;
}

export function proposedEnrollmentName(course, {custom1 = '', custom2 = '', customText = ''} = {}) {
    if (!course) return '';
    if (isNamedInSetup(course)) return normalizeName(course.title);
    const naming = optionNaming(course);
    if (naming) {
        const base = normalizeName(naming.prefix || course.title);
        return normalizeName([base, String(custom1 || '').replace('N/A', ''), String(custom2 || '').replace('N/A', '')].join(' '));
    }
    return normalizeName(course.title + ' ' + (customText || ''));
}

// Lowercased effective names of the student's enrollments (the list endpoint
// never returns deleted rows, the guard is belt-and-braces)
export function takenNamesFromEnrollments(enrollments, {excludeId = null} = {}) {
    const names = new Set();
    for (const e of (enrollments || [])) {
        if (!e || e.is_deleted) continue;
        if (excludeId && e.id === excludeId) continue;
        const name = normalizeName(e.custom_name || (e.courses && e.courses.title)).toLowerCase();
        if (name) names.add(name);
    }
    return names;
}
